import React, {useEffect, useId, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {IOption, Select} from "@pg-design/select-module";
import {Text} from "@pg-design/text-module";
import {CREDIT_PAGES_IDS} from "@pg-mono/e2e-test-utils";
import {FaqSection} from "@pg-mono/faq/src/components/FaqSection";
import {isEmpty} from "@pg-mono/nodash";

import {IRPStore} from "../../../app/rp_reducer";
import {CreditApplicationSource} from "../../../application/utils/CreditApplicationSource";
import {useGetCreditOffersQuery} from "../../api/credit_offers_api";
import {CreditOffersSortOptions, SortType} from "../../constants/CreditOffersSortOptions";
import {useCreditPaywallAbTest} from "../../hooks/use_credit_paywall_ab_test";
import {ModalEventSource} from "../../tracking/analytics/credit_modal_event";
import {CreditCalculatorGTMEvent} from "../../tracking/credit_gtm";
import {CreditAlgolyticsViewType} from "../../tracking/types/CreditAlgolyticsViewType";
import {ICreditData} from "../../types/ICreditData";
import {ICreditOffer} from "../../types/ICreditOffer";
import {ICreditOffersFilters} from "../../types/ICreditOffersFilters";
import {sortCreditOffers} from "../../utils/sort_credit_offers";
import {CreditApplicationModal} from "../CreditApplicationModal";
import {CreditOfferBox} from "./CreditOfferBox";
import {CreditOfferInfoText} from "./CreditOfferInfoText";
import {CreditOfferLeadBox} from "./CreditOfferLeadBox";

interface IProps {
    filters: ICreditOffersFilters;
    wasAppliedManually: boolean;
}

const getMinMaxOfferValues = (offerList: ICreditOffer[]) => {
    if (!Array.isArray(offerList) || offerList.length === 0) {
        return null;
    }

    const {min, max} = offerList.reduce(
        (acc, offer) => {
            const rate = offer.equal_installment_rate || 0;
            return {
                min: Math.min(acc.min, rate),
                max: Math.max(acc.max, rate)
            };
        },
        {min: Infinity, max: -Infinity}
    );

    return {min: Math.round(min), max: Math.round(max)};
};

export const CreditOffersList = (props: IProps) => {
    const {filters, wasAppliedManually} = props;
    const faq = useSelector((store: IRPStore) => store.faq.data);
    const selectInstanceId = useId();
    const {isPaywallVariant, areOffersUnlocked} = useCreditPaywallAbTest();

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [sort, setSort] = useState<IOption>(CreditOffersSortOptions[0]);
    const [offerList, setOfferList] = useState<ICreditOffer[]>([]);
    const [hasOpenedModalForFilters, setHasOpenedModalForFilters] = useState<boolean>(false);

    const applicationPayloadRef = useRef<ICreditData>({} as ICreditData);

    const {data, isFetching} = useGetCreditOffersQuery(
        {
            property_price: filters.price,
            own_contribution: filters.contribution,
            term_of_the_loan: filters.period,
            credit_amount: filters.credit_amount
        },
        {skip: isEmpty(filters) || Object.values(filters).includes(0)}
    );

    useEffect(() => {
        if (data?.results.length === 0) {
            setOfferList([]);
        }

        if (data?.results.length && data?.results.length > 0 && sort) {
            const sortedOffers = sortCreditOffers(data.results, sort.value as SortType);
            setOfferList(sortedOffers);
        }
    }, [data, sort]);

    // Automatically open modal after filters are applied in paywall variant (only when manually applied)
    useEffect(() => {
        if (isPaywallVariant && !areOffersUnlocked && wasAppliedManually && !isEmpty(filters) && offerList.length > 0 && !hasOpenedModalForFilters) {
            // Open modal automatically for the first offer
            const firstOffer = offerList[0];
            if (firstOffer) {
                const payload: ICreditData = {
                    source: CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT,
                    term_of_the_loan: firstOffer.maximum_term_of_the_loan_in_months / 12,
                    bank: firstOffer.bank,
                    mortgage_offer_name: firstOffer.offer_name.split(" - ")[0],
                    credit_amount: Math.ceil(firstOffer.max_loan_price),
                    property_price: filters.price,
                    own_contribution: filters.contribution,
                    contribution_percent: Number(((100 * filters.contribution) / filters.price).toFixed(1))
                };
                applicationPayloadRef.current = payload;
                setIsModalOpen(true);
                setHasOpenedModalForFilters(true);
            }
        }
    }, [isPaywallVariant, areOffersUnlocked, wasAppliedManually, filters, offerList, hasOpenedModalForFilters]);

    const openModal = (payload: ICreditData) => {
        applicationPayloadRef.current = {
            ...payload,
            property_price: filters.price,
            own_contribution: filters.contribution,
            contribution_percent: Number(((100 * filters.contribution) / filters.price).toFixed(1))
        };
        setIsModalOpen(true);
        setHasOpenedModalForFilters(true);
    };

    const {min, max} = useMemo(() => {
        const minMaxValues = getMinMaxOfferValues(offerList);
        if (!minMaxValues) {
            return {min: 0, max: 0};
        }
        return minMaxValues;
    }, [offerList]);

    const shouldShowLeadBox = areOffersUnlocked || !isPaywallVariant;

    return (
        <div className={containerStyle}>
            <div className={listHeaderStyle}>
                <div className={flexColumnStyle}>
                    <Text variant="headline_2" className={headlineStyle}>
                        {isFetching ? null : isEmpty(offerList) ? (
                            <>Nie znaleźliśmy ofert kredytu hipotecznego dla podanych parametrów</>
                        ) : (
                            <>Ranking kredytów hipotecznych według Twoich możliwości</>
                        )}
                    </Text>
                    <Text variant="body_copy_2" className={subHeadlineStyle}>
                        Znaleźliśmy dla Ciebie <strong id={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.OFFERS_VALUE}>{`${offerList.length}`} propozycji</strong>.
                        Poznaj szczegóły najlepszych ofert kredytów hipotecznych
                    </Text>
                </div>
                <Select
                    instanceId={selectInstanceId}
                    value={sort}
                    options={CreditOffersSortOptions}
                    onChange={(newValue) => setSort(newValue as IOption)}
                    className={selectStyle}
                />
            </div>

            {isFetching && (
                <div className={loaderStyle}>
                    <Loader size="lg" />
                </div>
            )}

            {!isFetching && !isEmpty(offerList) ? (
                <>
                    <div className={boxesWrapperStyle} id={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDIT_BOX_LIST}>
                        {offerList.map((offer, index) => {
                            return (
                                <React.Fragment key={offer.offer_id}>
                                    {shouldShowLeadBox && index === 2 && <CreditOfferLeadBox min={min} max={max} filterPrice={filters.price} />}
                                    <CreditOfferBox offer={offer} onButtonClick={openModal} />
                                </React.Fragment>
                            );
                        })}
                    </div>
                </>
            ) : null}

            <CreditApplicationModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                submitData={applicationPayloadRef.current}
                gtmEvent={
                    applicationPayloadRef.current.source === CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT
                        ? CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR
                        : CreditCalculatorGTMEvent.CREDIT_WORTHINESS_CALCULATOR
                }
                algolyticData={{
                    type: CreditAlgolyticsViewType.BANK_OFFERS,
                    source:
                        applicationPayloadRef.current.source === CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT
                            ? ModalEventSource.CREDIT_ASK
                            : ModalEventSource.WORTHINESS_CHECK
                }}
            />
            <CreditOfferInfoText />
            <FaqSection faqContent={faq} className={faqStyle} />
        </div>
    );
};

const containerStyle = css({
    position: "relative"
});

const listHeaderStyle = css({
    marginBottom: 3,
    md: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        minHeight: 6
    }
});

const flexColumnStyle = css({
    display: "flex",
    flexDirection: "column"
});

const headlineStyle = css({
    marginBottom: 2,
    md: {
        marginBottom: 0
    }
});

const subHeadlineStyle = css({
    marginTop: 0.5,
    marginBottom: 2,
    md: {
        marginBottom: 0
    }
});

const selectStyle = css({
    minWidth: "260px"
});

const loaderStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const boxesWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 3
});

const faqStyle = css({
    marginTop: 5,
    maxWidth: "90% !important"
});
import React, {useEffect, useId, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {IOption, Select} from "@pg-design/select-module";
import {Text} from "@pg-design/text-module";
import {CREDIT_PAGES_IDS} from "@pg-mono/e2e-test-utils";
import {FaqSection} from "@pg-mono/faq/src/components/FaqSection";
import {isEmpty} from "@pg-mono/nodash";

import {IRPStore} from "../../../app/rp_reducer";
import {CreditApplicationSource} from "../../../application/utils/CreditApplicationSource";
import {useGetCreditOffersQuery} from "../../api/credit_offers_api";
import {CreditOffersSortOptions, SortType} from "../../constants/CreditOffersSortOptions";
import {useCreditPaywallAbTest} from "../../hooks/use_credit_paywall_ab_test";
import {ModalEventSource} from "../../tracking/analytics/credit_modal_event";
import {CreditCalculatorGTMEvent} from "../../tracking/credit_gtm";
import {CreditAlgolyticsViewType} from "../../tracking/types/CreditAlgolyticsViewType";
import {ICreditData} from "../../types/ICreditData";
import {ICreditOffer} from "../../types/ICreditOffer";
import {ICreditOffersFilters} from "../../types/ICreditOffersFilters";
import {sortCreditOffers} from "../../utils/sort_credit_offers";
import {CreditApplicationModal} from "../CreditApplicationModal";
import {CreditOfferBox} from "./CreditOfferBox";
import {CreditOfferInfoText} from "./CreditOfferInfoText";
import {CreditOfferLeadBox} from "./CreditOfferLeadBox";

interface IProps {
    filters: ICreditOffersFilters;
    wasAppliedManually: boolean;
}

const getMinMaxOfferValues = (offerList: ICreditOffer[]) => {
    if (!Array.isArray(offerList) || offerList.length === 0) {
        return null;
    }

    const {min, max} = offerList.reduce(
        (acc, offer) => {
            const rate = offer.equal_installment_rate || 0;
            return {
                min: Math.min(acc.min, rate),
                max: Math.max(acc.max, rate)
            };
        },
        {min: Infinity, max: -Infinity}
    );

    return {min: Math.round(min), max: Math.round(max)};
};

export const CreditOffersList = (props: IProps) => {
    const {filters, wasAppliedManually} = props;
    const faq = useSelector((store: IRPStore) => store.faq.data);
    const selectInstanceId = useId();
    const {isPaywallVariant, areOffersUnlocked} = useCreditPaywallAbTest();

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [sort, setSort] = useState<IOption>(CreditOffersSortOptions[0]);
    const [offerList, setOfferList] = useState<ICreditOffer[]>([]);
    const [hasOpenedModalForFilters, setHasOpenedModalForFilters] = useState<boolean>(false);

    const applicationPayloadRef = useRef<ICreditData>({} as ICreditData);

    const {data, isFetching} = useGetCreditOffersQuery(
        {
            property_price: filters.price,
            own_contribution: filters.contribution,
            term_of_the_loan: filters.period,
            credit_amount: filters.credit_amount
        },
        {skip: isEmpty(filters) || Object.values(filters).includes(0)}
    );

    useEffect(() => {
        if (data?.results.length === 0) {
            setOfferList([]);
        }

        if (data?.results.length && data?.results.length > 0 && sort) {
            const sortedOffers = sortCreditOffers(data.results, sort.value as SortType);
            setOfferList(sortedOffers);
        }
    }, [data, sort]);

    // Automatically open modal after filters are applied in paywall variant (only when manually applied)
    useEffect(() => {
        if (isPaywallVariant && !areOffersUnlocked && wasAppliedManually && !isEmpty(filters) && offerList.length > 0 && !hasOpenedModalForFilters) {
            // Open modal automatically for the first offer
            const firstOffer = offerList[0];
            if (firstOffer) {
                const payload: ICreditData = {
                    source: CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT,
                    term_of_the_loan: firstOffer.maximum_term_of_the_loan_in_months / 12,
                    bank: firstOffer.bank,
                    mortgage_offer_name: firstOffer.offer_name.split(" - ")[0],
                    credit_amount: Math.ceil(firstOffer.max_loan_price),
                    property_price: filters.price,
                    own_contribution: filters.contribution,
                    contribution_percent: Number(((100 * filters.contribution) / filters.price).toFixed(1))
                };
                applicationPayloadRef.current = payload;
                setIsModalOpen(true);
                setHasOpenedModalForFilters(true);
            }
        }
    }, [isPaywallVariant, areOffersUnlocked, wasAppliedManually, filters, offerList, hasOpenedModalForFilters]);

    const openModal = (payload: ICreditData) => {
        applicationPayloadRef.current = {
            ...payload,
            property_price: filters.price,
            own_contribution: filters.contribution,
            contribution_percent: Number(((100 * filters.contribution) / filters.price).toFixed(1))
        };
        setIsModalOpen(true);
        setHasOpenedModalForFilters(true);
    };

    const {min, max} = useMemo(() => {
        const minMaxValues = getMinMaxOfferValues(offerList);
        if (!minMaxValues) {
            return {min: 0, max: 0};
        }
        return minMaxValues;
    }, [offerList]);

    const shouldShowLeadBox = areOffersUnlocked || !isPaywallVariant;

    return (
        <div className={containerStyle}>
            <div className={listHeaderStyle}>
                <div className={flexColumnStyle}>
                    <Text variant="headline_2" className={headlineStyle}>
                        {isFetching ? null : isEmpty(offerList) ? (
                            <>Nie znaleźliśmy ofert kredytu hipotecznego dla podanych parametrów</>
                        ) : (
                            <>Ranking kredytów hipotecznych według Twoich możliwości</>
                        )}
                    </Text>
                    <Text variant="body_copy_2" className={subHeadlineStyle}>
                        Znaleźliśmy dla Ciebie <strong id={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.OFFERS_VALUE}>{`${offerList.length}`} propozycji</strong>.
                        Poznaj szczegóły najlepszych ofert kredytów hipotecznych
                    </Text>
                </div>
                <Select
                    instanceId={selectInstanceId}
                    value={sort}
                    options={CreditOffersSortOptions}
                    onChange={(newValue) => setSort(newValue as IOption)}
                    className={selectStyle}
                />
            </div>

            {isFetching && (
                <div className={loaderStyle}>
                    <Loader size="lg" />
                </div>
            )}

            {!isFetching && !isEmpty(offerList) ? (
                <>
                    <div className={boxesWrapperStyle} id={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDIT_BOX_LIST}>
                        {offerList.map((offer, index) => {
                            return (
                                <React.Fragment key={offer.offer_id}>
                                    {shouldShowLeadBox && index === 2 && <CreditOfferLeadBox min={min} max={max} filterPrice={filters.price} />}
                                    <CreditOfferBox offer={offer} onButtonClick={openModal} />
                                </React.Fragment>
                            );
                        })}
                    </div>
                </>
            ) : null}

            <CreditApplicationModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                submitData={applicationPayloadRef.current}
                gtmEvent={
                    applicationPayloadRef.current.source === CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT
                        ? CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR
                        : CreditCalculatorGTMEvent.CREDIT_WORTHINESS_CALCULATOR
                }
                algolyticData={{
                    type: CreditAlgolyticsViewType.BANK_OFFERS,
                    source:
                        applicationPayloadRef.current.source === CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT
                            ? ModalEventSource.CREDIT_ASK
                            : ModalEventSource.WORTHINESS_CHECK
                }}
            />
            <CreditOfferInfoText />
            <FaqSection faqContent={faq} className={faqStyle} />
        </div>
    );
};

const containerStyle = css({
    position: "relative"
});

const listHeaderStyle = css({
    marginBottom: 3,
    md: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        minHeight: 6
    }
});

const flexColumnStyle = css({
    display: "flex",
    flexDirection: "column"
});

const headlineStyle = css({
    marginBottom: 2,
    md: {
        marginBottom: 0
    }
});

const subHeadlineStyle = css({
    marginTop: 0.5,
    marginBottom: 2,
    md: {
        marginBottom: 0
    }
});

const selectStyle = css({
    minWidth: "260px"
});

const loaderStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const boxesWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 3
});

const faqStyle = css({
    marginTop: 5,
    maxWidth: "90% !important"
});
